import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  View,
  Text,
  Modal,
  Animated,
  StyleSheet,
  TouchableOpacity
} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';

export const useToast = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const showToastMessage = useCallback((message) => {
    setToastMessage(message);
    setShowToast(true);
  }, []);

  const hideToast = useCallback(() => {
    setShowToast(false);
    setToastMessage('');
  }, []);

  return {
    showToast,
    toastMessage,
    showToastMessage,
    hideToast
  };
};

const Toast = ({
  visible,
  message,
  onHide,
  duration = 2000,
  colors = ['#d47d02', '#ffb636'],
  textColor = '#00000',
  persistent = false,
  onClose = null,
  noModal = false,
  bottomOffset = null
}) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      if (persistent) {
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }).start();
      } else {
        Animated.sequence([
          Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 300,
            useNativeDriver: true,
          }),
          Animated.delay(duration),
          Animated.timing(fadeAnim, {
            toValue: 0,
            duration: 300,
            useNativeDriver: true,
          }),
        ]).start(({finished}) => {
          if (finished) onHide();
        });
      }
    } else {
      fadeAnim.setValue(0);
    }
  }, [visible, fadeAnim, duration, onHide, persistent]);

  if (!visible) return null;

  const toastContent = (
    <Animated.View
      style={[
        styles.container,
        bottomOffset === null
          ? styles.topRightPosition
          : { bottom: bottomOffset, right: 20 },
        { opacity: fadeAnim }
      ]}
      pointerEvents={onClose ? 'auto' : 'none'}
    >
      <LinearGradient
        colors={colors}
        style={styles.toast}
      >
        {onClose && (
          <TouchableOpacity
            testID="ToastClose"
            onPress={onClose}
            style={styles.closeButton}
          >
            <i
              className="icon-cancel"
              style={{ color: textColor, fontSize: 16 }}
            />
          </TouchableOpacity>
        )}
        <Text
          style={[
            styles.text,
            { color: textColor },
            onClose && styles.textWithClose
          ]}
        >
          {message}
        </Text>
      </LinearGradient>
    </Animated.View>
  );

  if (noModal) {
    return toastContent;
  }

  return (
    <Modal
      transparent
      visible={visible}
      animationType="none"
      onRequestClose={onClose || onHide}
    >
      <View
        style={styles.modalOverlay}
        pointerEvents={onClose ? 'box-none' : 'none'}
      >
        {toastContent}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  container: {
    position: 'absolute',
    zIndex: 999,
    elevation: 10,
  },
  topRightPosition: {
    top: 20,
    right: 20,
  },
  toast: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 25,
    minWidth: 250,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  text: {
    fontSize: 14,
    fontFamily: 'Poppins_300Light',
    textAlign: 'center',
  },
  textWithClose: {
    paddingTop: 8,
    paddingRight: 10,
  },
  closeButton: {
    position: 'absolute',
    top: 4,
    right: 6,
    padding: 4,
    zIndex: 1,
  }
});

export default Toast;
